import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { getGallery } from '../../services/api';
import { adminApi } from '../../context/AdminContext';
import ImageUpload from '../../components/ImageUpload';

const AdminGallery = () => {
  const { toast } = useToast();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '', title_ar: '', category_en: '', category_ar: '', image_url: ''
  });

  const loadGallery = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getGallery();
      setGallery(data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load gallery", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title_en: '', title_ar: '', category_en: '', category_ar: '', image_url: '' });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title_en: item.title.en,
      title_ar: item.title.ar,
      category_en: item.category.en,
      category_ar: item.category.ar,
      image_url: item.image_url
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const galleryData = {
        title: { en: formData.title_en, ar: formData.title_ar },
        category: { en: formData.category_en, ar: formData.category_ar },
        image_url: formData.image_url,
        is_active: true,
        order: editingItem ? editingItem.order : gallery.length + 1
      };

      if (editingItem) {
        await adminApi.put(`/gallery/${editingItem.id}`, galleryData);
        toast({ title: "Success", description: "Gallery item updated" });
      } else {
        await adminApi.post(`/gallery`, galleryData);
        toast({ title: "Success", description: "Gallery item added" });
      }

      setIsDialogOpen(false);
      resetForm();
      loadGallery();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save gallery item", variant: "destructive" });
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await adminApi.delete(`/gallery/${itemId}`);
      toast({ title: "Success", description: "Gallery item deleted" });
      loadGallery();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete item", variant: "destructive" });
    }
  };

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Gallery Management</h2>
          <p className="text-gray-600 mt-1">Manage your portfolio images</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" data-testid="add-gallery-btn" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />Add Image
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Gallery Image' : 'Add Gallery Image'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title (English)</Label>
              <Input data-testid="gallery-title-en" value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} required />
            </div>
            <div>
              <Label>Title (Arabic)</Label>
              <Input data-testid="gallery-title-ar" value={formData.title_ar} onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })} required />
            </div>
            <div>
              <Label>Category (English)</Label>
              <Input data-testid="gallery-category-en" value={formData.category_en} onChange={(e) => setFormData({ ...formData, category_en: e.target.value })} required />
            </div>
            <div>
              <Label>Category (Arabic)</Label>
              <Input value={formData.category_ar} onChange={(e) => setFormData({ ...formData, category_ar: e.target.value })} required />
            </div>
            <ImageUpload label="Image" value={formData.image_url} onChange={(url) => setFormData({ ...formData, image_url: url })} />
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-gallery-btn">
                {editingItem ? 'Update Image' : 'Add Image'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.map((item) => (
            <Card key={item.id} className="overflow-hidden group" data-testid={`gallery-item-${item.id}`}>
              <div className="relative">
                <img src={item.image_url} alt={item.title.en} className="object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => handleEdit(item)} data-testid={`edit-gallery-${item.id}`}>
                    <Edit className="h-4 w-4 mr-1" />Edit
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-gallery-${item.id}`}>
                    <Trash2 className="h-4 w-4 mr-1" />Delete
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <div className="text-sm font-semibold">{item.title.en}</div>
                <div className="text-xs text-gray-500">{item.category.en}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
